import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Check, UserCog } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ROLE_PERSONAS, useActiveRole, setActiveRole, isFounder } from "@/lib/activeRole";

/**
 * ELIA · Founder-only profile switcher pill.
 * Renders nothing for non-founders. For founders: a compact selector that
 * lets them see the platform as any persona (Founder / Admin / Member / Pro / Institution).
 */
export function FounderProfileSwitcher() {
  const { user } = useAuth();
  const { active } = useActiveRole(user);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!isFounder(user)) return null;
  const current = ROLE_PERSONAS.find((r) => r.key === active) || ROLE_PERSONAS[0];

  const pick = async (r) => {
    setBusy(true);
    try {
      await setActiveRole(r.key, user);
      setOpen(false);
      if (r.path) navigate(r.path);
    } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="founder-role-switcher"
        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest transition-colors hover:opacity-90"
        style={{ border: "1px solid #C5A88044", color: "#FBF6EACC" }}
        title="Basculer entre les profils (fondateur uniquement)"
      >
        <UserCog className="h-3 w-3" strokeWidth={2} />
        <span className={current.tone}>{current.label}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[80]" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 top-full mt-2 w-64 rounded-lg border border-[#E4E1D8] bg-white shadow-xl z-[81] overflow-hidden"
            data-testid="founder-role-menu"
          >
            <div className="px-3 py-2 border-b border-[#E4E1D8] bg-[#F8F7F3]">
              <div className="text-[9px] font-mono uppercase tracking-widest text-[#7A5B14]">
                Bascule fondateur
              </div>
              <div className="mt-0.5 text-[10px] text-[#5B5B62] leading-snug">
                Voir la plateforme comme si vous étiez ce profil.
              </div>
            </div>
            {ROLE_PERSONAS.map((r) => (
              <button
                key={r.key}
                onClick={() => pick(r)}
                disabled={busy}
                data-testid={`founder-role-pick-${r.key}`}
                className={`w-full text-left px-3 py-2.5 text-sm hover:bg-[#F8F7F3] disabled:opacity-60 flex items-center justify-between transition-colors ${
                  r.key === active ? "bg-[#F8F7F3]" : ""
                }`}
              >
                <div>
                  <div className={`font-medium ${r.tone}`}>{r.label}</div>
                  <div className="text-[10px] font-mono text-[#8B8B8F] mt-0.5">{r.path}</div>
                </div>
                {r.key === active && <Check className="h-4 w-4 text-[#0B2545]" strokeWidth={2} />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * ELIA · Banner shown when the founder is impersonating a non-founder role.
 * Placed inline at the top of the primary layouts.
 */
export function ActiveRoleBanner() {
  const { user } = useAuth();
  const { active } = useActiveRole(user);
  if (!isFounder(user) || active === "founder") return null;
  const current = ROLE_PERSONAS.find((r) => r.key === active);
  return (
    <div
      className="w-full bg-[#FBF6EA] border-b border-[#C5A880]/40 px-4 py-2 flex items-center justify-center gap-3"
      data-testid="active-role-banner"
    >
      <span className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
        Vue courante : {current?.label || active}
      </span>
      <button
        onClick={() => setActiveRole("founder", user)}
        data-testid="active-role-banner-exit"
        className="text-[10px] font-mono uppercase tracking-widest text-[#0B2545] hover:underline"
      >
        ← Revenir Fondateur
      </button>
    </div>
  );
}
